<template>
  <div>
    <Loading v-if="isLoadingScreen" />
    <CRow>
      <CCol col="12" xl="12">
        <CAlert show color="warning" v-if="hasWarning">
          {{ hasWarning }}
        </CAlert>
        <CAlert v-if="hasNotice" color="info">
           {{ hasNotice }}
        </CAlert>
        <CCard>
          <CCardHeader>
            <CDropdown toggler-text="..." class="ml-1 float-right" color="secondary" :caret="false">
              <CDropdownItem @click="cancelChanges">Cancel changes</CDropdownItem>
              <CDropdownDivider />
              <CDropdownItem :disabled="isNew" @click="deleteBlock">Delete block</CDropdownItem>
            </CDropdown>

            <CButton :color="saveButton.color" :disabled="saveDisabled" @click="saveData" class="float-right">
              <CIcon :name="saveButton.icon" size="sm" class="mr-1" />{{ saveButton.text }}
            </CButton>
            <h3>
              {{ block.name || 'New Block' }}
            </h3>
          </CCardHeader>
          <CCardBody>
            <CNav variant="tabs" class="border-bottom">
              <CNavItem :active="tab == 'settings'" @click.native="tab = 'settings'">
                Block Settings
              </CNavItem>
              <CNavItem :active="tab == 'advanced'" @click.native="tab = 'advanced'">
                Advanced Settings
              </CNavItem>
              <CNavItem :active="tab == 'featured'" @click.native="tab = 'featured'">
                Featured info
              </CNavItem>
            </CNav>

            <div v-if="tab == 'settings'" class="pl-2 mt-4 border-bottom">
              <CRow>
                <CCol sm="3">
                  <CInput
                    label="Block name"
                    placeholder="Enter block name"
                    description="This name can appear above a block or detail screen in the app"
                    v-model="block.name"
                  />
                </CCol>
                <CCol sm="3">
                  <CSelect
                    label="Block style in screens"
                    :value.sync="block.block_type"
                    :options="settings.block_types"
                    @change="validateBlockType"
                    placeholder="Please select a block type"
                    description="The display style when this appears on a screen with multiple other blocks"
                  />
                </CCol>
                <CCol sm="3">
                  <CSelect 
                    :disabled="disable_detail_style"
                    label="Block style in detail screen"
                    :value.sync="block.detail_style"
                    :options="settings.detail_styles"
                    placeholder="Please select a block type"
                    description="The display style when this block is viewed separatetly"
                  />
                </CCol>
                <CCol sm="3">
                  <CInput
                    label="Maximum items"
                    type="number"
                    v-model="limit"
                    description="Limit the number of items to show when this appears on a screen with multiple other blocks. Detail screens can always show all items."
                  />
                </CCol>
              </CRow>
            </div>

            <div v-if="tab == 'advanced'">
              <CRow class="mt-4">
                <CCol sm="4">
                  <label>Onboarding text</label>
                  <TipTap v-model="block.onboarding"/>
                  <small class="text-muted">This text will display when the block is first shown in the (updated) app as part of the onboarding process.</small>
                </CCol>
                <CCol sm="4">
                  <CSelect
                      label="Hide block name on screen"
                      :value.sync="block.meta.hide_name"
                      :options="[false, true]"
                      description="When selected the block name will not be displayed when the block appears in a screen."
                  />
                </CCol>
                <CCol sm="4">
                  <CInput
                      label="'Red pill' text"
                      type="text"
                      v-model="pill_text"
                      description="This text will appear in a red pill besides the block name (e.g. BETA, NIEUW etc.)"
                  />
                </CCol>
              </CRow>
              <CRow class="mt-4">
                <CCol sm="4">
                  <CSelect
                    label="Block hook (optional)"
                    :value.sync="block.system_name"
                    :options="settings.block_system_names"
                    @change="validateSystemName"
                    placeholder="Connect this block to a special feature"
                    description="This is used to identify special blocks that are accessed by the system directly"
                  />
                </CCol>
                <CCol sm="4">
                  <CSelect
                      label="Condition (optional)"
                      :value.sync="block.condition_name"
                      :options="settings.block_condition_name_options"
                      description="The condition to check for inclusion in the screen"
                  />{{block.block_condition_name}}
                </CCol>
                <CCol sm="4" v-if="block.condition_name">
                  <CSelect
                      label="Include if condition is"
                      :value.sync="block.condition_value"
                      :options="settings.block_condition_value_options"
                      description="Include if condition matches this value, otherwise exclude"
                  />
                </CCol>
                <CCol sm="4">
                  <ImageUpload
                    label="Android Auto Icon"
                    v-model="android_auto_icon"
                    description="This will be used as the block's icon in Android Auto"
                    :width=150
                    :height=150
                    background_color="#000"
                  />
                </CCol>
              </CRow>
            </div>

            <div v-if="tab == 'featured'" class="pl-2 mt-4">
              <CAlert show color="info">
                    <CIcon name="cil-lightbulb"/>
                    If the information below is provided, it will be displayed in the header of the block detail screen.
                </CAlert>
              <CRow>
                <CCol xl="7" lg="6">
                  <div>
                    <label>Rich text title</label>
                    <TipTap v-model="block.title"/>
                  </div>
                  <div class="mt-4">
                    <label>Rich text description</label>
                    <TipTap v-model="block.description"/>
                  </div>
                  <div class="mt-4">
                    <CSelect
                      label="Text color"
                      :value.sync="block.text_color"
                      :options="settings.text_colors"
                    />
                  </div>
                  <div class="mt-4">
                    <ImageUpload
                      label="Block image"
                      v-model="block.image_url"
                      description="This square image will display on top of the background image (1:1 ratio)"
                      :width=150
                      :height=150
                    />
                  </div>
                  <div class="mt-4">
                    <ImageUpload
                      label="Background image"
                      v-model="block.background_image_url"
                      description="The background image, 550x900 pixels"
                      :width=183
                      :height=300
                    />
                  </div>
                </CCol>
                <CCol xl="5" lg="6">
                  Preview:
                  <FeaturedPreview
                    :image_url="block.image_url"
                    :background_image_url="block.background_image_url"
                    :text_color="block.text_color"
                    :title="block.title"
                    :description="block.description"
                  />
                </CCol>
              </CRow>
            </div>

            <div v-if="tab == 'settings'" class="pl-2 mt-4">
              <CRow v-if="!isNew">
                <CCol>
                  <CCardGroup deck>
                    <CCard v-if="!showAutofill">
                      <CCardHeader>
                        <strong>Add items to block</strong>
                      </CCardHeader>
                      <CCardBody>
                        <CNav variant="tabs" class="border-bottom">
                          <CNavItem :active="item_type == 'podcast'" @click.native="getItems('podcast')" v-if="validItemTab('podcast')">
                            Podcasts
                          </CNavItem>
                          <CNavItem :active="item_type == 'episode'" @click.native="getItems('episode')" v-if="validItemTab('episode')">
                            Episodes
                          </CNavItem>
                          <CNavItem :active="item_type == 'link'" @click.native="getItems('link')"  v-if="validItemTab('link')">
                            Links
                          </CNavItem>
                        </CNav>

                        <CInput v-model="search" :placeholder="`Search ` + item_type" class="mt-4">
                          <template #prepend-content><CIcon name="cil-magnifying-glass"/></template>
                        </CInput>

                        <CListGroup v-if="!showAutofill">
                          <div class="d-flex justify-content-center" v-if="isLoading">
                            <div class="spinner-border" role="status">
                              <span class="sr-only">Loading...</span>
                            </div>
                          </div>
                          <CListGroupItem
                            v-for="(item, index) in filterAllItems"
                            :key="index"
                            @click="addItem(item.item_id)"
                            style="cursor:pointer"
                          >
                            <img :src="item.image_url || pixel" class="list_image" v-if="item.content_type !== 'link'" />
                            {{ item.title }}
                            <CBadge :color="getBadge(item.content_type)">
                              {{ item.content_type }}
                            </CBadge>
                            <cIcon class="float-right" name="cilPlaylistAdd" />
                          </CListGroupItem>
                        </CListGroup>
                        <CPagination
                          class="items-pager"
                          v-if="pages > 1"
                          :dots="false"
                          size="sm"
                          align="center"
                          v-on:update:activePage="pageChanged"
                          :active-page="activePage"
                          :pages="pages"
                        />
                      </CCardBody>
                    </CCard>

                    <CCard>
                      <CCardHeader>
                        <strong>Items in {{ block.name }} block</strong>
                      </CCardHeader>
                      <CCardBody>
                        <CNav variant="tabs" class="border-bottom">
                          <CNavItem :active="!showAutofill" @click.native="doCurate">
                            Curated
                          </CNavItem>
                          <CNavItem :active="showAutofill" @click.native="doAutofill" v-if="!autofillDisabled">
                            Auto-fill
                          </CNavItem>
                        </CNav>

                        <CSelect
                          v-if="showAutofill"
                          label=""
                          :value.sync="autofill"
                          :options="settings.autofillOptions"
                          @change="autofillChange"
                          placeholder="Please select an autofill function"
                          class="mt-4"
                        />

                        <ul
                          v-if="items && items.length > 0 && !showAutofill"
                          class="list-group mt-4"
                          v-sortable="$options.sortOptions"
                          @sorted="moveItem"
                        >
                          <li v-for="(item, index) in items" :key="item.item_id" :class="itemClass(index, item)">
                            <CRow>
                              <CCol sm="10" class="sort-handle">
                                <img
                                  :src="item.image_url || pixel"
                                  class="list_image"
                                  v-if="item.content_type !== 'link'"
                                />
                                {{ item.title }}
                                <CBadge :color="getBadge(item.content_type)">
                                  {{ item.content_type }}
                                </CBadge>
                                <CBadge class="ml-1" v-if="['carousel', 'exclusive_carousel'].includes(block.block_type) && !item.short_review" color="danger">
                                  Missing a review!
                                </CBadge>
                                <CBadge class="ml-1" v-if="block.block_type == 'featured_podcast' && !item.introduction" color="danger">
                                  Missing a featured introduction text!
                                </CBadge>
                                <CBadge class="ml-1" v-if="block.block_type == 'featured_podcast' && !item.background_image_url" color="danger">
                                  Missing a background image!
                                </CBadge>
                              </CCol>
                              <CCol sm="2">
                                <CDropdown toggler-text="..." class="float-right item-menu" :caret="false" size="sm">
                                  <CDropdownItem @click="removeItem(item.item_id)">Remove item</CDropdownItem>
                                  <CDropdownDivider />
                                  <CDropdownItem @click="goItem(item.id, item.content_type)">Go to item</CDropdownItem>
                                </CDropdown>
                              </CCol>
                            </CRow>
                          </li>
                        </ul>

                        <CAlert color="info" v-if="autofill != 'none' && autofill != 'latest_from_block_podcasts'" class="mt-4">
                          Items from auto-fill function <strong><em>{{ autofillName }}</em></strong> will be appended after any curated items
                        </CAlert>

                        <CAlert color="info" v-if="autofill == 'latest_from_block_podcasts'" class="mt-4">
                          Auto-fill function <strong><em>{{ autofillName }}</em></strong> will display the most recent episode for each podcast listed above.
                        </CAlert>

                        <CAlert color="warning" v-if="items && items.length == 0 && !showAutofill" class="mt-4">
                          No items yet. Click an item on the left to add it to this block.
                        </CAlert>

                        <CListGroup v-if="autofill_items && autofill_items.length > 0 && showAutofill">
                          <CListGroupItem v-for="(item, index) in autofill_items" :key="index" class="autofill-items">
                            <img :src="item.image_url || pixel" class="list_image" />
                            {{ item.title }}
                            <CBadge :color="getBadge(item.content_type)">
                              {{ item.content_type }}
                            </CBadge>
                          </CListGroupItem>
                        </CListGroup>

                        <CAlert color="warning" v-if="autofillchanged">
                          Save this block to preview items
                        </CAlert>
                      </CCardBody>
                    </CCard>
                  </CCardGroup>
                </CCol>
              </CRow>
            </div>
          </CCardBody>
          <CCardFooter>
            <span class="float-right"> Block ID: {{ block.id }}</span>
          </CCardFooter>
        </CCard>
      </CCol>
    </CRow>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import _ from 'lodash'
import Sortable from '../../directives/Sortable'
import { PIXEL } from '../../constants'
import TipTap from '../../components/TipTap.vue'
import Loading from '../../components/Loading'
import ImageUpload from '../../components/ImageUpload'
import FeaturedPreview from '../../components/FeaturedPreview'

export default {
  name: 'Block',
  // mixins: [mixin],
  components: {
    TipTap,
    Loading,
    ImageUpload,
    FeaturedPreview,
  },
  sortOptions: {
    draggable: '.list-group-item',
    handle: '.sort-handle',
  },
  directives: { Sortable },
  data() {
    return {
      isNew: false,
      saveButtons: [
        { color: 'secondary', text: 'Saved', icon: 'cil-check-alt' },
        { color: 'danger', text: 'Save Block', icon: 'cil-save' },
      ],
      search: '',
      itemHighlightIndex: [],
      pixel: PIXEL,
      item_type: 'episode',
      showAutofill: false,
      autofillDisabled: false,
      autofillchanged: false,
      hasWarning: false,
      hasNotice: false,
      settings: this.$settings,
      activePage: 1,
      isLoadingScreen: true,
      validTypes: [],
      tab: 'settings',
      beforeChangeBlock: JSON.stringify({}),
      beforeChangeItems: JSON.stringify([]),
      disable_detail_style: false,
    }
  },
  mounted: function() {
    // close warning if it is for a block
    this.$store.dispatch('closeWarningsOfType', 'block')
    this.getData()
  },
  watch: {
    block: {
      deep: true,
      handler(val, oldVal) {
        if (val.id != 0) {
          this.isLoadingScreen = false
          this.validateSystemName()
        }
        if (!oldVal.block_type && val.block_type) {
          this.validateBlockType()
        }
        if (oldVal.id !== val.id) {
          this.beforeChangeBlock = JSON.stringify(val)
          return
        }
      },
    },
    items: {
      deep: true,
      handler(val, oldVal) {
        if (oldVal.length == 0 && val.length > 0) {
          this.beforeChangeItems = JSON.stringify(val)
        }
      }
    },
    search: {
      handler() {
        this.triggerSearch()
      },
    },
  },
  destroyed() {
    if (this.block.id != 0 &&
      (JSON.stringify(this.block) !== this.beforeChangeBlock ||
      JSON.stringify(this.items) !== this.beforeChangeItems))
    {
      this.$store.dispatch('issueWarning', {
        id: `block-${this.block.id}`,
        text: `You have unsaved changes in block '${this.block.name}' (${this.block.id}).`,
        location: `/blocks/${this.block.id}`,
      })
    }
    this.$store.dispatch('closeError')
  },
  computed: {
    saveDisabled() {
      let disabled = true
      if (this.block.name && this.block.block_type && this.block.detail_style) {
        disabled = false
      }
      if (this.block.block_type == 'featured_podcast' && this.items.find(it => !it.introduction || !it.background_image_url)) {
        disabled = true
      }
      if (this.block.id != 0 &&
        (JSON.stringify(this.block) == this.beforeChangeBlock &&
        JSON.stringify(this.items) == this.beforeChangeItems))
      {
        disabled = true
      }
      return disabled
    },
    autofillName() {
      return this.settings.autofillOptions.find( ({ value }) => value === this.autofill )["label"]
    },
    pages() {
      if (this.totalItems !== 0) return Math.ceil(this.totalItems / 10)
      return 1
    },
    saveButton() {
      return this.saveButtons[this.saveDisabled ? 0 : 1]
    },
    filterAllItems() {
      // filter items already moved to list
      return this.filteredAllItems
    },
    hide_name: {
      get() {
        if (this.$store.state.block.block) return this.$store.state.block.block.meta.hide_name
        return false
      },
      set() {
        this.$store.commit('setHidenameBlock')
      },
    },
    limit: {
      get() {
        if (this.$store.state.block.block) return this.$store.state.block.block.limit
        return false
      },
      set(limit) {
        this.$store.commit('setLimitBlock', { limit })
      },
    },
    autofill: {
      get() {
        if (this.$store.state.block.block) return this.$store.state.block.block.meta.autofill
        return ''
      },
      set(autofill) {
        this.$store.commit('setAutofillBlock', { autofill })
      },
    },
    pill_text: {
      get() {
        if (this.$store.state.block.block) return this.$store.state.block.block.meta.pill_text
        return null
      },
      set(pill_text) {
        if (pill_text.length < 1) {
          pill_text = null
        }
        this.$store.commit('setPilltextBlock', { pill_text })
      },
    },
    android_auto_icon: {
      get() {
        if (this.$store.state.block.block) return this.$store.state.block.block.meta.android_auto_icon
        return null
      },
      set(android_auto_icon) {
        if (android_auto_icon.length < 1) {
          android_auto_icon = null
        }
        this.$store.commit('setAndroidAutoIconBlock', { android_auto_icon })
      },
    },
    ...mapState({
      block: state => state.block.block,
      items: state => state.block.block.items,
      autofill_items: state => state.block.block.autofill_items,
      useAutofill(state) {
        if (state.block.block && state.block.block.meta.autofill !== 'none') {
          return true
        } else if (this.showAutofill) {
          return true
        }
        return false
      },
    }),
    ...mapGetters(['bblockById', 'isLoading', 'allItems', 'totalItems', 'filteredAllItems']),
  },
  methods: {
    getData() {
      const id = this.$route.params.id
      if (this.block.id != id) {
        this.$store.commit('clearBlock')
      }
      if (id == 0) {
        this.isNew = true
        this.$store.commit('clearBlock')
        this.getItems()
        this.isLoadingScreen = false
        return
      }

      if (this.$route.query && this.$route.query.page) {
        this.activePage = Number(this.$route.query.page)
      }

      const block = this.bblockById(id)
      if (block) {
        // opening a previous opened block
        this.isLoadingScreen = false
        return
      }

      this.$store.dispatch('getBlock', {
        $api: this.$api,
        id,
      })
    },

    saveData() {
      this.hasWarning = false
      if (this.block.system_name == "none") {
        this.block.system_name = null
      }
      // tiptap wraps it in a p, let's strip that
      ['title', 'description', 'onboarding'].forEach(key => {
        if (this.block[key]) {
          this.block[key] = this.block[key].replace(/<p>/g, '').replace(/<\/p>/g, '')
        } else {
          this.block[key] = ''
        }
      })
      this.dispatchSave()
    },

    dispatchSave() {
      if (this.isNew) {
        this.$store.dispatch('newBlock', { $api: this.$api, $router: this.$router }).then(() => {
          this.isNew = false
        })
      } else {
        this.$store.dispatch('saveBlock', { $api: this.$api }).then(() => {
          this.beforeChangeBlock = JSON.stringify(this.block)
          this.beforeChangeItems = JSON.stringify(this.items)
        })
      }
    },

    maxWarning(max) {
      return `For this block, only the first ${max} items will be displayed on the app screen. Any additional items will only show if the user navigates to the block detail screen.`
    },

    addItem(item_id) {
      this.$store.commit('addItem', { item_id })
      if (this.items.length > this.block.limit) {
        this.hasWarning = this.maxWarning(this.block.limit)
      }
    },

    removeItem(item_id) {
      this.$store.commit('removeItem', { item_id })
      this.getItems(this.item_type)
    },

    moveItem(ev) {
      const { oldIndex, newIndex } = ev
      this.$store.commit('moveItem', { oldIndex, newIndex })
    },

    getItems(item_type = 'episode', page = 1) {
      this.item_type = item_type
      this.activePage = page
      this.$store.dispatch('getAllItems', {
        $api: this.$api,
        search: this.search,
        type: item_type,
        page,
        limit: 10,
      })
    },

    deleteBlock() {
      if (confirm('This will delete this block. Are you sure?')) {
        this.$store.dispatch('deleteBlock', {
          $api: this.$api,
          $router: this.$router,
        })
      }
    },

    pageChanged(val) {
      this.getItems(this.item_type, val)
      this.$router.push({ query: { page: val } }).catch(error => {
        if (
          error.name !== 'NavigationDuplicated' &&
          !error.message.includes('Avoided redundant navigation to current location')
        ) {
          throw error
        }
      })
    },

    getBadge(status) {
      switch (status) {
        case 'podcast':
          return 'success'
        case 'episode':
          return 'secondary'
        case 'audiobook':
          return 'warning'
        case 'link':
          return 'danger'
        default:
          'primary'
      }
    },

    itemHighlight(index) {
      if (this.itemHighlightIndex.includes(index)) {
        return 'success'
      }
      return ''
    },

    autofillChange() {
      if (this.autofill === 'none') {
        // this.items = []
        this.$store.commit('resetItems')
      } else {
        this.autofillchanged = true
      }
    },

    doCurate() {
      this.showAutofill = false
    },

    doAutofill() {
      this.showAutofill = true
      if (!this.useAutofill) {
        this.autofill = 'none'
      }
    },

    triggerSearch: _.debounce(function() {
      this.getItems(this.item_type)
    }, 500),

    cancelChanges() {
      this.$store.commit('clearBlock')
      this.$router.push({ path: `/blocks` })
    },

    goItem(id, type) {
      if (type === 'episode') {
        this.$router.push({ path: `/podcasts/episodes/${id}` })
      }
      if (type === 'podcast') {
        this.$router.push({ path: `/podcasts/podcasts/${id}` })
      }
      if (type === 'link') {
        this.$router.push({ path: `/links/${id}` })
      }
    },

    itemClass(index, item) {
      if ((index+1) > this.block.limit) {
        return 'max-items list-group-item'
      }
      if (!item.short_review && this.block.block_type == 'carousel') {
        return 'no-review list-group-item'
      }
      return 'list-group-item'
    },

    validItemTab(item_type) {
      if (this.validTypes.length == 0) {
        return true
      }
      return this.validTypes.includes(item_type)
    },

    validateBlockType() {
      if (["links", "bundle_block_links"].includes(this.block.block_type)) {
        this.validTypes = ["link"]
        this.getItems('link')
        this.hasNotice = "This Block style may only contain link items."
        this.autofillDisabled = true
      } else if (["thin_episode_carousel"].includes(this.block.block_type)) {
        this.validTypes = ["episode"]
        this.getItems('episode')
        this.hasNotice = "This Block style may only contain episode items."
        this.autofillDisabled = false
      } else if (["featured_episodes"].includes(this.block.block_type)) {
        this.validTypes = ["episode", "podcast"]
        this.getItems('episode')
        this.hasNotice = "This Block style may only contain episode items, or podcast items in combination with the auto-fill setting 'Show most recent episode for selected podcasts'."
        this.autofillDisabled = false
      } else if (["featured_podcast"].includes(this.block.block_type)) {
        this.validTypes = ["podcast"]
        this.getItems('podcast')
        this.hasNotice = "This Block style may only contain podcast items."
        this.autofillDisabled = false
        this.limit = 1
      } else {
        this.validTypes = []
        this.hasNotice = false
        this.autofillDisabled = false
        if (this.items.length == 0) {
          this.getItems('episode')
        }
      }
    },
    validateSystemName() {
      if (this.block.system_name == 'continue_listening') {
        this.block.detail_style = 'list'
        this.disable_detail_style = true
      }
    }
  },
}
</script>

<style>
.items-pager {
  margin-top: 12px;
}
.list_image {
  width: 36px;
  height: 36px;
}
.autofill-items {
  background-color: #e0e0e0;
  opacity: 0.5;
}
.sort-handle {
  cursor: grab;
}
.max-items {
  background-color: #ebedef;
}
.no-review {
  background-color: #f4a4a4;
}
</style>
